import { useState, useEffect, useCallback, useMemo } from 'react';

export type Breakpoint = 'mobile' | 'tablet-portrait' | 'tablet-landscape' | 'desktop';

const BREAKPOINTS = {
  'tablet-portrait': 768,
  'tablet-landscape': 1024,
  'desktop': 1280,
} as const;

function getBreakpoint(width: number): Breakpoint {
  if (width < BREAKPOINTS['tablet-portrait']) return 'mobile';
  if (width < BREAKPOINTS['tablet-landscape']) return 'tablet-portrait';
  if (width < BREAKPOINTS['desktop']) return 'tablet-landscape';
  return 'desktop';
}

/**
 * Returns the current viewport breakpoint, updated on resize.
 * Uses matchMedia listeners for performance (no resize polling).
 */
export function useBreakpoint(): Breakpoint {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(() => getBreakpoint(window.innerWidth));

  useEffect(() => {
    // Use matchMedia for efficient, debounce-free breakpoint detection
    const queries = [
      { mq: window.matchMedia(`(max-width: ${BREAKPOINTS['tablet-portrait'] - 1}px)`), bp: 'mobile' as Breakpoint },
      { mq: window.matchMedia(`(min-width: ${BREAKPOINTS['tablet-portrait']}px) and (max-width: ${BREAKPOINTS['tablet-landscape'] - 1}px)`), bp: 'tablet-portrait' as Breakpoint },
      { mq: window.matchMedia(`(min-width: ${BREAKPOINTS['tablet-landscape']}px) and (max-width: ${BREAKPOINTS['desktop'] - 1}px)`), bp: 'tablet-landscape' as Breakpoint },
      { mq: window.matchMedia(`(min-width: ${BREAKPOINTS['desktop']}px)`), bp: 'desktop' as Breakpoint },
    ];

    const handlers = queries.map(({ mq, bp }) => {
      const handler = (e: MediaQueryListEvent) => {
        if (e.matches) setBreakpoint(bp);
      };
      mq.addEventListener('change', handler);
      return { mq, handler };
    });

    // Set initial value
    for (const { mq, bp } of queries) {
      if (mq.matches) {
        setBreakpoint(bp);
        break;
      }
    }

    return () => {
      handlers.forEach(({ mq, handler }) => mq.removeEventListener('change', handler));
    };
  }, []);

  return breakpoint;
}

/** True when viewport is below 768px */
export function useIsMobile(): boolean {
  const bp = useBreakpoint();
  return bp === 'mobile';
}

/** True when viewport is below 1024px (mobile + tablet-portrait) */
export function useIsTablet(): boolean {
  const bp = useBreakpoint();
  return bp === 'mobile' || bp === 'tablet-portrait';
}

/** True when viewport is below 1280px (anything not desktop) */
export function useIsCompact(): boolean {
  const bp = useBreakpoint();
  return bp !== 'desktop';
}
